import React, { FC, useMemo } from 'react';
import moment from 'moment-timezone';
import { isNil } from 'lodash';
import {
	HistoryOutlined,
	ProfileOutlined,
	SyncOutlined,
	TransactionOutlined,
} from '@ant-design/icons';
import {
	getWalkthroughScoreObject,
	nullSafeGet,
	nullSafeGetOrElse,
	nullSafeGetWithZero,
} from '../../utils/DataAccessUtils';
import { floatToPercentageString, myTimeZone } from '../../utils/DataFormatterUtils';
import {
	getWalkthroughAnsweredSummary,
	getWalkthroughDurationMinutes,
} from '../../utils/WalkthroughComparisonUtils';
import { formatDurationShort } from '../life_cycle_analytics_overview_page/LifeCycleAnalyticsUtils';
import OWStatCard from '../ow_stat_card/OWStatCard';
const POSITIVE_COLOR = '#389E0D';
const NEGATIVE_COLOR = '#CF1322';

interface StatTileProps {
	icon: React.ReactNode;
	label: string;
	value: string;
	valueSuffix?: string;
	footer?: React.ReactNode;
}

const StatTile: FC<StatTileProps> = ({ icon, label, value, valueSuffix, footer }) => (
	<OWStatCard className="flex min-w-0 flex-col gap-1 rounded border-0 bg-grey-1">
		<div className="footnoteregular flex flex-row items-center gap-2 text-character-secondary">
			{icon}
			<span className="truncate">{label}</span>
		</div>
		<div className="flex flex-row items-baseline gap-2">
			<span className="h4medium text-character-primary">{value}</span>
			{valueSuffix ? <span className="h5regular text-character-primary">{valueSuffix}</span> : null}
		</div>
		<div className="footnoteregular min-h-[20px] text-character-secondary">{footer}</div>
	</OWStatCard>
);

const ComparisonDelta: FC<{
	delta: number | null;
	baselineLabel?: string;
	higherIsBetter?: boolean;
	unit?: string;
}> = ({ delta, baselineLabel, higherIsBetter = true, unit = '%' }) => {
	if (delta === null) return null;
	const isHigher = delta >= 0;
	const isFavourable = higherIsBetter ? isHigher : !isHigher;
	return (
		<span className="footnoteregular text-character-secondary">
			<span style={{ color: isFavourable ? POSITIVE_COLOR : NEGATIVE_COLOR }}>
				{`${isHigher ? '+' : ''}${delta}${unit}`}
			</span>
			{` ${isHigher ? 'over' : 'under'} past 3 avg${baselineLabel ? ` (${baselineLabel})` : ''}`}
		</span>
	);
};

interface WalkthroughStatTilesProps {
	walkthrough: any;
	history: any;
	isUnscored?: boolean;
}

export const WalkthroughStatTiles: FC<WalkthroughStatTilesProps> = ({
	walkthrough,
	history,
	isUnscored = false,
}) => {
	const answeredSummary = useMemo(() => getWalkthroughAnsweredSummary(walkthrough), [walkthrough]);
	const overallScoreObject = getWalkthroughScoreObject(walkthrough);
	const durationMinutes = getWalkthroughDurationMinutes(walkthrough);

	const isComplete = nullSafeGet('status', walkthrough) === 'complete';
	const timeZoneId = nullSafeGet('location.timeZoneId', walkthrough);

	const rawCurrentPercentage = nullSafeGetWithZero('percentageScore', overallScoreObject);
	const currentPercentage =
		rawCurrentPercentage === null || rawCurrentPercentage === undefined
			? null
			: Number(rawCurrentPercentage);

	const averageOverall = nullSafeGet('averages.overall', history);
	const rawAveragePercentage = nullSafeGetWithZero('percentageScore', averageOverall);
	const averagePercentage =
		rawAveragePercentage === null || rawAveragePercentage === undefined
			? null
			: Number(rawAveragePercentage);
	const averageDurationMinutes = nullSafeGetWithZero('averages.durationMinutes', history);

	const previousRuns = nullSafeGetOrElse('previousWalkthroughs', history, []).map((previous) => {
		const percentageScore = nullSafeGetWithZero('overall.percentageScore', previous);
		const completedAt = nullSafeGet('completedAt', previous);
		return {
			id: nullSafeGet('id', previous),
			label: completedAt
				? moment(completedAt)
						.tz(timeZoneId || myTimeZone())
						.format('MMM D')
				: '--',
			value: isNil(percentageScore)
				? '--'
				: floatToPercentageString(Number(percentageScore) / 100, 0),
		};
	});

	const scoreDelta =
		isComplete && !isNil(currentPercentage) && !isNil(averagePercentage)
			? Math.round(currentPercentage - averagePercentage)
			: null;
	const durationDelta =
		isNil(durationMinutes) || isNil(averageDurationMinutes) || averageDurationMinutes === 0
			? null
			: Math.round(((durationMinutes - averageDurationMinutes) * 100) / averageDurationMinutes);

	const currentScore = nullSafeGetWithZero('score', overallScoreObject);
	const currentMaxScore = nullSafeGetWithZero('maxPossibleScore', overallScoreObject);
	const averageScore = nullSafeGetWithZero('score', averageOverall);
	const averageMaxScore = nullSafeGetWithZero('maxPossibleScore', averageOverall);

	const getAnsweredFooter = () => {
		if (answeredSummary.totalCount === 0) return 'Walkthrough has not been conducted yet';
		if (answeredSummary.incompleteSectionCount > 0) {
			const noun = answeredSummary.incompleteSectionCount === 1 ? 'section' : 'sections';
			return `${answeredSummary.incompleteSectionCount} ${noun} incomplete`;
		}
		const unansweredCount = answeredSummary.totalCount - answeredSummary.answeredCount;
		if (unansweredCount > 0) {
			const noun = unansweredCount === 1 ? 'question' : 'questions';
			return `${unansweredCount} ${noun} unanswered`;
		}
		return 'All sections answered';
	};

	return (
		<div className="flex flex-row flex-wrap gap-2">
			<StatTile
				icon={isComplete ? <TransactionOutlined /> : <SyncOutlined />}
				label={isComplete ? 'Walkthrough complete' : 'Walkthrough in progress'}
				value={`${answeredSummary.answeredCount}/${answeredSummary.totalCount}`}
				valueSuffix="Answered"
				footer={getAnsweredFooter()}
			/>
			{!isUnscored && (
				<StatTile
					icon={<ProfileOutlined />}
					label="Score"
					value={isNil(currentPercentage) ? '--' : floatToPercentageString(currentPercentage / 100)}
					valueSuffix={
						isNil(currentScore) || isNil(currentMaxScore)
							? ''
							: `(${currentScore}/${currentMaxScore})`
					}
					footer={
						<span className="flex flex-row flex-wrap items-center gap-2">
							<ComparisonDelta delta={scoreDelta} unit=" pts" />
						</span>
					}
				/>
			)}
			{!isUnscored && (
				<StatTile
					icon={<ProfileOutlined />}
					label="Avg Score (Last 3)"
					value={isNil(averagePercentage) ? '--' : floatToPercentageString(averagePercentage / 100)}
					valueSuffix={
						isNil(averageScore) || isNil(averageMaxScore)
							? ''
							: `(${averageScore}/${averageMaxScore})`
					}
					footer={
						<span className="flex flex-wrap items-center gap-x-3 gap-y-1">
							{previousRuns.map((previousRun) => (
								<span key={previousRun.id}>
									<span className="text-character-secondary">{`${previousRun.label}: `}</span>
									<span className="text-character-primary">{previousRun.value}</span>
								</span>
							))}
						</span>
					}
				/>
			)}
			<StatTile
				icon={<HistoryOutlined />}
				label="Time To Complete"
				value={
					isComplete && !isNil(durationMinutes) ? formatDurationShort(durationMinutes * 60) : '--'
				}
				footer={
					isComplete ? (
						<span className="flex flex-row flex-wrap items-center gap-2">
							<ComparisonDelta
								delta={durationDelta}
								higherIsBetter={false}
								baselineLabel={
									averageDurationMinutes === null || averageDurationMinutes === undefined
										? undefined
										: `${averageDurationMinutes}m`
								}
							/>
						</span>
					) : (
						'In progress'
					)
				}
			/>
		</div>
	);
};

export default WalkthroughStatTiles;
